/**
 * VideosTab — listado, subida y reproducción de vídeos del expediente.
 *
 * Cada vídeo se sube al endpoint genérico `/expedientes/{id}/attachments/upload`
 * con `kind=video`. Tras la subida, el backend lanza una compresión asíncrona
 * (ffmpeg → H.264 720p) que reemplaza el archivo. Mientras está procesándose,
 * la fila muestra un spinner y el chip "Comprimiendo…".
 *
 * Patrón visual paralelo a `PhotosTab.jsx` (subida por dropzone y bandeja).
 */
import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import {
  UploadSimple, FilmStrip, Trash, Spinner, Play, Download as DownloadIcon,
  CheckCircle, WarningCircle, FilmReel, Camera,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getToken } from "@/lib/authToken";
import FolderBrowser from "./FolderBrowser";

const fmtSize = (b) => {
  if (!b && b !== 0) return "—";
  return b > 1024 * 1024
    ? (b / 1024 / 1024).toFixed(1) + " MB"
    : (b / 1024).toFixed(0) + " KB";
};

const fmtDuration = (sec) => {
  if (sec == null) return "—";
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

// Límite de subida (cliente). Mantiene UX previsible — el backend no impone
// límite duro, pero el ingress puede cortar a 200-500 MB. Avisamos antes.
const MAX_VIDEO_MB = 500;

export default function VideosTab({ expedienteId, onCountChange }) {
  const [items, setItems] = useState([]);
  const [drag, setDrag] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [preview, setPreview] = useState(null);
  const [currentFolder, setCurrentFolder] = useState(""); // raíz por defecto
  const inputRef = useRef(null);
  const token = getToken();

  const load = async () => {
    const r = await api.get(`/expedientes/${expedienteId}/attachments?kind=video`);
    setItems(r.data);
    onCountChange?.();
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [expedienteId]);

  // Polling cada 4s si hay vídeos comprimiéndose, para actualizar estado.
  useEffect(() => {
    const procesando = items.some((v) =>
      v.video_compress_status === "pending" || v.video_compress_status === "processing"
    );
    if (!procesando) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const moveItem = async (attId, folder) => {
    await api.patch(`/expedientes/${expedienteId}/attachments/${attId}`, { folder });
    load();
  };

  const validate = (files) => {
    const arr = Array.from(files || []);
    const valid = [];
    for (const f of arr) {
      const isVideo = (f.type || "").startsWith("video/") || /\.(mp4|mov|m4v|webm|mkv|avi|3gp|wmv)$/i.test(f.name);
      if (!isVideo) { toast.error(`"${f.name}" no es un vídeo`); continue; }
      if (f.size > MAX_VIDEO_MB * 1024 * 1024) {
        toast.error(`"${f.name}" supera los ${MAX_VIDEO_MB} MB`);
        continue;
      }
      valid.push(f);
    }
    return valid;
  };

  const upload = async (files) => {
    const valid = validate(files);
    if (!valid.length) return;
    setUploading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      valid.forEach((f) => fd.append("files", f));
      fd.append("kind", "video");
      // Si hay carpeta seleccionada (subcarpeta), úsala; si no, "Videos".
      fd.append("folder", currentFolder || "Videos");
      await api.post(`/expedientes/${expedienteId}/attachments/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setUploadPct(Math.round((e.loaded * 100) / e.total));
        },
      });
      toast.success(
        valid.length === 1
          ? "Vídeo subido — comprimiendo en segundo plano"
          : `${valid.length} vídeos subidos — comprimiendo en segundo plano`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir vídeo");
    } finally {
      setUploading(false);
      setUploadPct(0);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files);
  };

  const del = async (id, name) => {
    if (!window.confirm(`¿Eliminar el vídeo "${name}"?`)) return;
    try {
      await api.delete(`/expedientes/${expedienteId}/attachments/${id}`);
      toast("Vídeo eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const videoSrc = (v) => `${API}/expedientes/${expedienteId}/attachments/${v.id}/file?token=${token}`;
  const thumbSrc = (v) =>
    v.video_thumb_path
      ? `${API}/expedientes/${expedienteId}/attachments/${v.id}/thumb?token=${token}`
      : null;

  return (
    <div className="space-y-4" data-testid="videos-tab">
      {/* Dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="videos-dropzone"
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          drag
            ? "border-brand bg-brand/5"
            : "border-border bg-white hover:border-foreground/60"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          multiple
          onChange={(e) => upload(e.target.files)}
          className="hidden"
          data-testid="videos-file-input"
        />
        <FilmStrip className="w-8 h-8 mx-auto text-muted mb-2" weight="duotone" />
        <div className="text-[13px] font-semibold text-foreground">
          {uploading
            ? `Subiendo ${uploadPct}%…`
            : "Arrastra vídeos aquí o pulsa para seleccionar"}
        </div>
        <div className="text-[11px] text-muted mt-1">
          Se comprimen automáticamente a 720p H.264 · máx. {MAX_VIDEO_MB} MB por vídeo
        </div>
        {uploading && uploadPct > 0 && (
          <div className="mt-3 h-1 bg-zinc-100 rounded overflow-hidden">
            <div
              className="h-1 bg-brand transition-all"
              style={{ width: `${uploadPct}%` }}
            />
          </div>
        )}
      </div>

      {/* Lista con sidebar de carpetas + drag&drop. Se muestra siempre
          para permitir crear carpetas aunque no haya vídeos aún. */}
      <div className="min-h-[300px]">
        <FolderBrowser
          expedienteId={expedienteId}
          kind="video"
          items={items}
          onMoveItem={moveItem}
          renderGrid={(filtered, folder) =>
            filtered.length === 0 ? (
              <div className="text-[12.5px] text-muted italic text-center py-10 bg-white border border-dashed border-border rounded-lg">
                {items.length === 0
                  ? "Aún no hay vídeos en este expediente."
                  : folder
                    ? "Carpeta vacía. Arrastra vídeos aquí o súbelos desde arriba."
                    : "No hay vídeos en la raíz."}
              </div>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="videos-list">
                {filtered.map((v) => (
                  <VideoCard
                    key={v.id}
                    v={v}
                    thumbSrc={thumbSrc(v)}
                    onPlay={() => setPreview(v)}
                    onDelete={() => del(v.id, v.filename)}
                  />
                ))}
              </ul>
            )
          }
        />
      </div>

      {/* Player modal */}
      {preview && (
        <VideoPlayerModal
          v={preview}
          src={videoSrc(preview)}
          expedienteId={expedienteId}
          onClose={() => setPreview(null)}
          onFotoCaptured={load}
        />
      )}
    </div>
  );
}

function VideoCard({ v, thumbSrc, onPlay, onDelete }) {
  const status = v.video_compress_status;
  const processing = status === "pending" || status === "processing";

  return (
    <li
      data-testid={`video-card-${v.id}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-att-id", v.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      className="bg-white border border-border rounded-lg overflow-hidden flex flex-col cursor-move"
    >
      <button
        type="button"
        onClick={onPlay}
        className="relative aspect-video bg-zinc-900 group focus:outline-none"
        data-testid={`video-play-${v.id}`}
        disabled={processing && !thumbSrc}
      >
        {thumbSrc ? (
          <img
            src={thumbSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <FilmReel className="w-10 h-10 text-zinc-500" weight="duotone" />
          </div>
        )}
          {!processing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-md">
              <Play className="w-5 h-5 text-foreground ml-0.5" weight="fill" />
            </div>
          </div>
          )}
        {processing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40">
            <Spinner className="w-6 h-6 text-white animate-spin" weight="bold" />
            <span className="text-white text-[11px] font-semibold mt-1.5">Comprimiendo…</span>
          </div>
        )}
        {v.video_duration_sec != null && !processing && (
          <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10.5px] font-mono px-1.5 py-0.5 rounded">
            {fmtDuration(v.video_duration_sec)}
          </span>
        )}
      </button>

      <div className="p-2.5 flex-1 flex flex-col gap-1">
        <div className="text-[12.5px] font-semibold text-foreground line-clamp-1" title={v.filename}>
          {v.filename}
        </div>
        <div className="text-[10.5px] text-muted inline-flex items-center gap-2 flex-wrap">
          <span>{fmtSize(v.size_bytes)}</span>
          {v.compressed && v.original_size_bytes && (
            <span
              title={`Original ${fmtSize(v.original_size_bytes)}`}
              className="inline-flex items-center gap-0.5 text-emerald-700"
            >
              <CheckCircle className="w-2.5 h-2.5" weight="bold" />
              {(100 - Math.round((v.size_bytes / v.original_size_bytes) * 100))}% menos
            </span>
          )}
          {status === "failed" && (
            <span className="inline-flex items-center gap-0.5 text-amber-700" title="No se pudo comprimir; el original sigue disponible">
              <WarningCircle className="w-2.5 h-2.5" weight="bold" /> Sin comprimir
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <a
            href={`${API}/expedientes/${v.expediente_id}/attachments/${v.id}/file?inline=false&token=${getToken()}`}
            download={v.filename}
            data-testid={`video-download-${v.id}`}
            className="text-[11px] inline-flex items-center gap-1 text-muted hover:text-foreground"
          >
            <DownloadIcon className="w-3 h-3" weight="bold" /> Descargar
          </a>
          <button
            onClick={onDelete}
            data-testid={`video-delete-${v.id}`}
            className="text-[11px] inline-flex items-center gap-1 text-muted hover:text-rose-700 ml-auto"
          >
            <Trash className="w-3 h-3" weight="bold" /> Eliminar
          </button>
        </div>
      </div>
    </li>
  );
}

function VideoPlayerModal({ v, src, expedienteId, onClose, onFotoCaptured }) {
  const videoRef = useRef(null);
  const [capturing, setCapturing] = useState(false);

  const captureFrame = async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) {
      toast.error("El vídeo aún no está listo");
      return;
    }
    setCapturing(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("No se pudo generar la imagen"))),
          "image/jpeg",
          0.9,
        );
      });

      // Caption: nombre del vídeo + timestamp del frame
      const sec = Math.floor(video.currentTime || 0);
      const mm = Math.floor(sec / 60);
      const ss = String(sec % 60).padStart(2, "0");
      const baseName = v.filename.replace(/\.[^.]+$/, "");
      const filename = `${baseName}_${mm}m${ss}s.jpg`;
      const caption = `Fotograma de "${v.filename}" en ${mm}:${ss}`;

      const file = new File([blob], filename, { type: "image/jpeg" });
      const fd = new FormData();
      fd.append("files", file);
      fd.append("kind", "photo");
      fd.append("folder", "Fotos");
      await api.post(`/expedientes/${expedienteId}/attachments/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Añadimos el caption con un PATCH (el upload no acepta caption inline).
      try {
        const list = await api.get(`/expedientes/${expedienteId}/attachments?kind=photo`);
        const just = (list.data || []).find((p) => p.filename === filename);
        if (just?.id) {
          await api.patch(`/expedientes/${expedienteId}/attachments/${just.id}`, { caption });
        }
      } catch { /* sin caption es aceptable */ }

      toast.success("Fotograma guardado en Fotos");
      onFotoCaptured?.();
    } catch (e) {
      toast.error(e?.message || "No se pudo capturar el fotograma");
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-player-modal"
    >
      <div
        className="bg-black rounded-lg shadow-xl w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800 rounded-t-lg gap-2">
          <span className="text-white text-[12.5px] font-semibold truncate flex-1">{v.filename}</span>
          <button
            onClick={captureFrame}
            disabled={capturing}
            data-testid="video-capture-frame"
            className="text-[11.5px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/10 text-white hover:bg-white/20 border border-white/20 font-semibold disabled:opacity-50"
            title="Pausa el vídeo en el fotograma deseado y captúralo como foto"
          >
            {capturing ? (
              <Spinner className="w-3.5 h-3.5 animate-spin" weight="bold" />
            ) : (
              <Camera className="w-3.5 h-3.5" weight="bold" />
            )}
            {capturing ? "Guardando…" : "Capturar fotograma"}
          </button>
          <button
            onClick={onClose}
            data-testid="video-player-close"
            className="text-white/70 hover:text-white text-xl leading-none w-7 h-7 inline-flex items-center justify-center"
          >
            ×
          </button>
        </div>
        <video
          ref={videoRef}
          src={src}
          controls
          autoPlay
          playsInline
          className="w-full max-h-[80vh] bg-black rounded-b-lg"
          data-testid="video-player-element"
        />
      </div>
    </div>
  );
}
